<!-- PropertySubdivision.vue -->
<template>
  <div class="modal fade property-subdivision-modal" tabindex="-1" role="dialog" aria-labelledby="myLargeModalLabel"
       aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-uppercase" id="myLargeModalLabel">Property Sub-Division</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="step === 'info'" class="row">
            <div class="col-12 mb-3">
              <div class="alert alert-info">
                <h5 class="alert-heading">Property Sub-Division</h5>
                <p>You are about to subdivide the property with LR Number: <strong>{{ property.LRNumber }}</strong></p>
                <p>Current Size: <strong>{{ property.percelSize }} Ha</strong></p>
                <p>Current Owner: <strong>{{ property.ownerNames }}</strong></p>
                <hr>
                <p class="mb-0">Please specify how many sub-plots you want to create and their details.</p>
              </div>
            </div>

            <!-- Balance Check Alert -->
            <div v-if="hasOutstandingBalance" class="col-12 mb-3">
              <div class="alert alert-danger">
                <h5 class="alert-heading">
                  <i class="bx bx-error-circle me-2"></i>
                  Subdivision Not Allowed
                </h5>
                <p><strong>This property cannot be subdivided because it has an outstanding balance.</strong></p>
                <div class="row mt-3">
                  <div class="col-md-6">
                    <strong>Financial Summary:</strong>
                    <ul class="mt-2">
                      <li v-if="property.landRatesArrears > 0">Land Rates Arrears: <span class="text-danger">KES {{ numberFormat(property.landRatesArrears) }}</span></li>
                      <li v-if="property.groundRentArrears > 0">Ground Rent Arrears: <span class="text-danger">KES {{ numberFormat(property.groundRentArrears) }}</span></li>
                      <li v-if="property.accumInterest > 0">Accumulated Interest: <span class="text-warning">KES {{ numberFormat(property.accumInterest) }}</span></li>
                      <li v-if="property.accumPenalty > 0">Accumulated Penalty: <span class="text-warning">KES {{ numberFormat(property.accumPenalty) }}</span></li>
                    </ul>
                  </div>
                  <div class="col-md-6">
                    <div class="card border-danger">
                      <div class="card-body text-center">
                        <h4 class="text-danger mb-0">KES {{ numberFormat(property.currentBalance) }}</h4>
                        <small class="text-muted">Total Outstanding Balance</small>
                      </div>
                    </div>
                  </div>
                </div>
                <hr>
                <p class="mb-0"><strong>Action Required:</strong> Please clear all outstanding balances before attempting to subdivide this property.</p>
              </div>
            </div>

            <div v-if="!hasOutstandingBalance" class="col-12 mb-3">
              <div class="form-group">
                <label for="numSubplots" class="form-label">Number of Sub-Plots</label>
                <input
                    v-model.number="numSubplots"
                    type="number"
                    class="form-control"
                    id="numSubplots"
                    min="2"
                    max="10"
                >
              </div>
            </div>

            <div class="col-12 mb-3">
              <button
                  v-if="!hasOutstandingBalance"
                  @click="generateSubplots"
                  class="btn btn-primary"
                  :disabled="numSubplots < 2"
              >
                Continue
              </button>
              <button
                  v-else
                  @click="closeModal"
                  class="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>

          <div v-else-if="step === 'form'" class="row">
            <div class="col-12 mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Parent Property: {{ property.LRNumber }} ({{ property.percelSize }} Ha)</h6>
                <span class="badge bg-info">Remaining Size: {{ remainingSize.toFixed(2) }} Ha</span>
              </div>
              <hr>
            </div>

            <div class="col-12 mb-4">
              <div v-for="(subplot, index) in subplots" :key="index" class="card mb-3">
                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Sub-Plot {{ index + 1 }}</h6>
                  <button
                      v-if="subplots.length > 2"
                      @click="removeSubplot(index)"
                      type="button"
                      class="btn btn-sm btn-danger"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label class="form-label">LR Number</label>
                        <input
                            v-model="subplot.LRNumber"
                            type="text"
                            class="form-control"
                            placeholder="Enter new LR Number"
                            required
                        >
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label class="form-label">Size (Ha)</label>
                        <input
                            v-model.number="subplot.percelSize"
                            type="number"
                            class="form-control"
                            placeholder="Enter size in Ha"
                            step="0.01"
                            required
                            @input="updateRemainingSize"
                        >
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label class="form-label">Owner</label>
                        <div class="form-check mb-2">
                          <input
                              class="form-check-input"
                              type="radio"
                              :name="'ownerType' + index"
                              :id="'sameOwner' + index"
                              value="same"
                              v-model="subplot.ownerType"
                          >
                          <label class="form-check-label" :for="'sameOwner' + index">
                            Same as parent
                          </label>
                        </div>
                        <div class="form-check">
                          <input
                              class="form-check-input"
                              type="radio"
                              :name="'ownerType' + index"
                              :id="'newOwner' + index"
                              value="new"
                              v-model="subplot.ownerType"
                          >
                          <label class="form-check-label" :for="'newOwner' + index">
                            New owner
                          </label>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6" v-if="subplot.ownerType === 'new'">
                      <div class="form-group mb-3">
                        <label class="form-label">Owner Name</label>
                        <input
                            v-model="subplot.ownerNames"
                            type="text"
                            class="form-control"
                            placeholder="Enter owner name"
                        >
                      </div>
                    </div>

                    <div v-if="subplot.ownerType === 'new'" class="col-md-6">
                      <div class="form-group mb-3">
                        <label class="form-label">Owner Phone</label>
                        <input
                            v-model="subplot.ownerPhoneNo"
                            type="text"
                            class="form-control"
                            placeholder="Enter phone number"
                        >
                      </div>
                    </div>

                    <div v-if="subplot.ownerType === 'new'" class="col-md-6">
                      <div class="form-group mb-3">
                        <label class="form-label">Owner Email</label>
                        <input
                            v-model="subplot.ownerEmail"
                            type="email"
                            class="form-control"
                            placeholder="Enter email address"
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Document Upload Section -->
            <div class="col-12 mb-4">
              <div class="card border-primary">
                <div class="card-header bg-primary text-white">
                  <h6 class="mb-0 text-white">
                    <i class="bx bx-file-plus me-2"></i>
                    Subdivision Documentation
                  </h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label">Upload Supporting Documents</label>
                    <p class="text-muted small">Upload subdivision approval documents, survey plans, or other relevant documentation (PDF, DOC, DOCX, JPG, PNG - Max 10MB per file)</p>
                    <input
                        ref="fileInput"
                        type="file"
                        class="form-control"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        @change="handleFileUpload"
                    >
                  </div>

                  <div v-if="uploadedFiles.length > 0" class="mb-3">
                    <label class="form-label">Uploaded Documents:</label>
                    <div class="uploaded-files">
                      <div v-for="(file, index) in uploadedFiles" :key="index" class="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                        <div class="d-flex align-items-center">
                          <i class="bx bx-file me-2 text-primary"></i>
                          <div>
                            <div class="fw-semibold">{{ file.name }}</div>
                            <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                          </div>
                        </div>
                        <button
                            @click="removeFile(index)"
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                        >
                          <i class="bx bx-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="subdivisionNotes" class="form-label">Additional Notes</label>
                    <textarea
                        v-model="subdivisionNotes"
                        id="subdivisionNotes"
                        class="form-control"
                        rows="3"
                        placeholder="Enter any additional notes or comments about the subdivision..."
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 mb-3 d-flex justify-content-between">
              <button @click="addSubplot" class="btn btn-info">
                <i class="bx bx-plus"></i> Add Another Sub-Plot
              </button>

              <div>
                <button @click="step = 'info'" class="btn btn-secondary me-2">Back</button>
                <button
                    @click="validateAndProceed"
                    class="btn btn-primary"
                    :disabled="!isFormValid"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>

          <div v-else-if="step === 'confirm'" class="row">
            <div class="col-12 mb-3">
              <div class="alert alert-warning">
                <h5 class="alert-heading">Confirm Sub-Division</h5>
                <p>You are about to subdivide property <strong>{{ property.LRNumber }}</strong> into {{ subplots.length }} new properties.</p>
                <p>This action cannot be undone. Please review the details below:</p>
              </div>
            </div>

            <div class="col-12 mb-3">
              <div class="table-responsive">
                <table class="table table-bordered">
                  <thead class="table-light">
                  <tr>
                    <th>New LR Number</th>
                    <th>Size (Ha)</th>
                    <th>Owner</th>
                    <th>Contact</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(subplot, index) in subplots" :key="index">
                    <td>{{ subplot.LRNumber }}</td>
                    <td>{{ subplot.percelSize }}</td>
                    <td>{{ subplot.ownerType === 'same' ? property.ownerNames : subplot.ownerNames }}</td>
                    <td>{{ subplot.ownerType === 'same' ? property.ownerPhoneNo : subplot.ownerPhoneNo }}</td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <td colspan="1"><strong>Total</strong></td>
                    <td><strong>{{ totalSize.toFixed(2) }} Ha</strong></td>
                    <td colspan="2">Original size: {{ property.percelSize }} Ha</td>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <!-- Documentation Summary -->
            <div v-if="uploadedFiles.length > 0 || subdivisionNotes" class="col-12 mb-3">
              <div class="card border-info">
                <div class="card-header bg-info text-white">
                  <h6 class="mb-0 text-white">Documentation Summary</h6>
                </div>
                <div class="card-body">
                  <div v-if="uploadedFiles.length > 0" class="mb-3">
                    <strong>Uploaded Documents ({{ uploadedFiles.length }}):</strong>
                    <ul class="mt-2 mb-0">
                      <li v-for="file in uploadedFiles" :key="file.name">{{ file.name }}</li>
                    </ul>
                  </div>
                  <div v-if="subdivisionNotes">
                    <strong>Notes:</strong>
                    <p class="mt-2 mb-0">{{ subdivisionNotes }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 mb-3 d-flex justify-content-between">
              <button @click="step = 'form'" class="btn btn-secondary">Back</button>
              <button @click="submitSubdivision" class="btn btn-success" :disabled="submitting">
                <span v-if="submitting">
                  <i class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i> Processing...
                </span>
                <span v-else>
                  Confirm Sub-Division
                </span>
              </button>
            </div>
          </div>

          <div v-else-if="step === 'success'" class="row">
            <div class="col-12 mb-3 text-center">
              <div class="success-image mb-4">
                <i class="bx bx-check-circle text-success" style="font-size: 5rem;"></i>
              </div>
              <h4 class="text-success">Sub-Division Completed Successfully</h4>
              <p>The property has been successfully subdivided into {{ subplots.length }} new properties.</p>
              <p>The original property's status has been updated to "Subdivided".</p>
              <div v-if="uploadedFiles.length > 0" class="mt-3">
                <div class="alert alert-info">
                  <i class="bx bx-info-circle me-2"></i>
                  {{ uploadedFiles.length }} document(s) have been successfully uploaded and linked to this subdivision.
                </div>
              </div>
            </div>

            <div class="col-12 mb-3 text-center">
              <button @click="closeModal" class="btn btn-primary">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {base, execute, landRate} from "@/api";

export default {
  name: "PropertySubdivision",
  props: {
    property: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      step: 'info',
      numSubplots: 2,
      subplots: [],
      submitting: false,
      remainingSize: 0,
      uploadedFiles: [],
      subdivisionNotes: '',
      maxFileSize: 10 * 1024 * 1024 // 10MB
    };
  },
  computed: {
    totalSize() {
      return this.subplots.reduce((total, subplot) => total + (subplot.percelSize || 0), 0);
    },
    isFormValid() {
      if (this.remainingSize < 0) return false;

      for (const subplot of this.subplots) {
        if (!subplot.LRNumber || !subplot.percelSize) return false;
        if (subplot.ownerType === 'new' && !subplot.ownerNames) return false;
      }

      return true;
    },
    hasOutstandingBalance() {
      return (this.property.currentBalance || 0) > 0;
    }
  },
  watch: {
    property: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.percelSize) {
          this.remainingSize = parseFloat(newVal.percelSize);
        }
      }
    }
  },
  methods: {
    generateSubplots() {
      this.subplots = [];
      const equalSize = parseFloat((this.property.percelSize / this.numSubplots).toFixed(2));

      for (let i = 0; i < this.numSubplots; i++) {
        this.subplots.push({
          LRNumber: `${this.property.LRNumber}/${String.fromCharCode(65 + i)}`, // A, B, C, etc.
          percelSize: equalSize,
          ownerType: 'same',
          ownerNames: '',
          ownerEmail: '',
          ownerPhoneNo: '',
          subCountyID: this.property.subCountyID,
          subCountyName: this.property.subCountyName,
          wardID: this.property.wardID,
          wardName: this.property.wardName,
          zone: this.property.zone,
          block: this.property.block,
          useName: this.property.useName,
          percelAddress: this.property.percelAddress,
          lat: this.property.lat,
          lng: this.property.lng,
          // Calculate proportional financial values
          outstandingArrears: parseFloat((this.property.outstandingArrears / this.numSubplots).toFixed(2)),
          annualCharges: parseFloat((this.property.annualCharges / this.numSubplots).toFixed(2)),
          landRatesArrears: parseFloat(((this.property.landRatesArrears || 0) / this.numSubplots).toFixed(2)),
          groundRentArrears: parseFloat(((this.property.groundRentArrears || 0) / this.numSubplots).toFixed(2)),
          accumPenalty: parseFloat(((this.property.accumPenalty || 0) / this.numSubplots).toFixed(2)),
          accumInterest: parseFloat(((this.property.accumInterest || 0) / this.numSubplots).toFixed(2)),
          annualLandRates: parseFloat(((this.property.annualLandRates || 0) / this.numSubplots).toFixed(2)),
          annualGroundRent: parseFloat(((this.property.annualGroundRent || 0) / this.numSubplots).toFixed(2))
        });
      }

      this.updateRemainingSize();
      this.step = 'form';
    },
    updateRemainingSize() {
      this.remainingSize = parseFloat(this.property.percelSize) - this.totalSize;
    },
    addSubplot() {
      if (this.subplots.length >= 10) {
        alert("Maximum of 10 sub-plots allowed");
        return;
      }

      this.subplots.push({
        LRNumber: `${this.property.LRNumber}/${String.fromCharCode(65 + this.subplots.length)}`,
        percelSize: 0,
        ownerType: 'same',
        ownerNames: '',
        ownerEmail: '',
        ownerPhoneNo: '',
        subCountyID: this.property.subCountyID,
        subCountyName: this.property.subCountyName,
        wardID: this.property.wardID,
        wardName: this.property.wardName,
        zone: this.property.zone,
        block: this.property.block,
        useName: this.property.useName,
        percelAddress: this.property.percelAddress,
        lat: this.property.lat,
        lng: this.property.lng,
        outstandingArrears: 0,
        annualCharges: 0,
        landRatesArrears: 0,
        groundRentArrears: 0,
        accumPenalty: 0,
        accumInterest: 0,
        annualLandRates: 0,
        annualGroundRent: 0
      });
    },
    removeSubplot(index) {
      this.subplots.splice(index, 1);
      this.updateRemainingSize();
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      const validFiles = [];

      for (const file of files) {
        // Validate file size
        if (file.size > this.maxFileSize) {
          alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
          continue;
        }

        // Validate file type
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          alert(`File "${file.name}" is not a supported format. Please upload PDF, DOC, DOCX, JPG, or PNG files.`);
          continue;
        }

        validFiles.push(file);
      }

      this.uploadedFiles.push(...validFiles);

      // Clear the input so the same file can be selected again if needed
      event.target.value = '';
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num || 0);
    },
    validateAndProceed() {
      // Check if all LR numbers are unique
      const lrNumbers = this.subplots.map(plot => plot.LRNumber);
      const uniqueLrNumbers = new Set(lrNumbers);

      if (uniqueLrNumbers.size !== this.subplots.length) {
        alert("All LR Numbers must be unique");
        return;
      }

      // Check if total size is equal to original property size (with small margin of error)
      const sizeDifference = Math.abs(this.property.percelSize - this.totalSize);
      if (sizeDifference > 0.01) {
        alert(`The total size of sub-plots (${this.totalSize.toFixed(2)} Ha) does not match the original property size (${this.property.percelSize} Ha)`);
        return;
      }

      this.step = 'confirm';
    },
    submitSubdivision() {
      this.submitting = true;

      // Prepare the data for each subplot
      const subplotsData = this.subplots.map(subplot => {
        const data = { ...subplot };

        // If same owner, copy parent property owner details
        if (subplot.ownerType === 'same') {
          data.ownerNames = this.property.ownerNames;
          data.ownerEmail = this.property.ownerEmail;
          data.ownerPhoneNo = this.property.ownerPhoneNo;
          data.ownerID = this.property.ownerID;
          data.ownerPassport = this.property.ownerPassport;
          data.ownerDrivingLicence = this.property.ownerDrivingLicence;
          data.pinNumber = this.property.pinNumber;
          data.boxNo = this.property.boxNo;
          data.postalCode = this.property.postalCode;
          data.town = this.property.town;
          data.physicalAddress = this.property.physicalAddress;
        }

        return data;
      });

      // Create form data
      const formData = new FormData();
      formData.append("function", "subdivideProperty");
      formData.append("parentPropertyID", this.property.id);
      formData.append("parentLRNumber", this.property.LRNumber);
      formData.append("subplots", JSON.stringify(subplotsData));
      formData.append("subdivisionNotes", this.subdivisionNotes);
      formData.append("sessionNames", sessionStorage.getItem("sessionNames"));
      formData.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      // Append files
      this.uploadedFiles.forEach((file, index) => {
        formData.append(`subdivisionDoc_${index}`, file);
      });
      formData.append("totalFiles", this.uploadedFiles.length.toString());

      execute(formData, landRate)
          .then((res) => {
            this.submitting = false;

            if (res.data.success) {
              this.step = 'success';
            } else {
              alert(res.data.message || "Failed to complete subdivision");
            }
          })
          .catch((error) => {
            this.submitting = false;
            console.error("Error:", error);
            alert("An error occurred while processing your request. Please try again.");
          });
    },
    closeModal() {
      // Reset the form
      this.step = 'info';
      this.numSubplots = 2;
      this.subplots = [];
      this.uploadedFiles = [];
      this.subdivisionNotes = '';

      // Refresh the property list
      this.$emit('refresh');

      // Close the modal
      const modal = document.querySelector('.property-subdivision-modal');
      if (modal) {
        const modalInstance = bootstrap.Modal.getInstance(modal);
        if (modalInstance) {
          modalInstance.hide();
        }
      }
    }
  }
};
</script>

<style scoped>
.uploaded-files {
  max-height: 200px;
  overflow-y: auto;
}

.card-header.bg-primary {
  background-color: #007bff !important;
}

.card-header.bg-info {
  background-color: #17a2b8 !important;
}

.success-image {
  animation: bounceIn 0.6s ease-in-out;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>